import React, { useEffect, useState, useMemo } from 'react';
import { Box, TextField, InputAdornment, Chip, Tooltip } from '@mui/material';
import { SearchIcon } from 'lucide-react';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchGroupCategories } from '../../store/coaStore/coaThunk';
import { ApiStatus, defaultRowsPerPageOptions } from '../../utils/api.types';
import CustomDataGrid from '../../components/DataGrid/DataGrid';

const GroupCategories: React.FC = () => {
  const dispatch = useAppDispatch();
  const { groupCategories, groupCategoriesTotal, status } = useAppSelector(state => state.coa);

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    dispatch(fetchGroupCategories({ page, limit: pageSize, search: searchQuery }));
  }, [dispatch, page, pageSize, searchQuery]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const getNatureColor = (nature: string) => {
    switch (nature) {
      case 'Asset': return { color: '#488dff', bg: '#f1f0fc' };
      case 'Liability': return { color: '#c33b3b', bg: '#fbecec' };
      case 'Equity': return { color: '#9c27b0', bg: '#f3e5f5' };
      case 'Income': return { color: '#49a563', bg: '#edf6ef' };
      case 'Expense': return { color: '#ff9800', bg: '#fff3e0' };
      default: return { color: '#545454', bg: '#f5f5f5' };
    }
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'accountingCode',
      headerName: 'Code',
      flex: 0.8,
      renderCell: ({ row }: GridValueGetterParams) => row.accountingCode || '-'
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.5,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Tooltip title={row.name}>
          <strong>{row.name}</strong>
        </Tooltip>
      )
    },
    {
      field: 'nature',
      headerName: 'Nature',
      flex: 0.8,
      renderCell: ({ row }: GridValueGetterParams) => {
        if (!row.nature) return '-';
        const colors = getNatureColor(row.nature);
        return (
          <Chip
            size="small"
            label={row.nature}
            style={{ borderRadius: 7, color: colors.color, backgroundColor: colors.bg }}
          />
        );
      }
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 3,
      renderCell: ({ row }: GridValueGetterParams) => {
        const desc = row.description || '-';
        return (
          <span style={{ whiteSpace: 'normal', lineHeight: '1.4' }}>{desc}</span>
        );
      }
    }
  ], []);

  return (
    <>
      {/* <Box sx={{ display: "flex", flexDirection: "column", width: '100%', pt: 2, pb: 1, gap: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: '100%' }}>
          <TextField
            className='c-text-field-4'
            variant="standard"
            style={{ width: '40%' }}
            size='small'
            placeholder='Search Group Categories...'
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ marginTop: -3 }} size={20} />
                </InputAdornment>
              ),
              style: { fontSize: 14, fontWeight: 'normal' },
            }}
          />
        </Box>
      </Box> */}

      <div style={{ height: "calc(100vh - 200px)", width: "100%" }}>
        <CustomDataGrid
          columns={columns}
          rows={groupCategories}
          rowCount={groupCategoriesTotal}
          rowsPerPageOptions={defaultRowsPerPageOptions}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          getRowId={(row) => row._id}
          loading={status === ApiStatus.LOADING}
        />
      </div>
    </>
  );
};

export default GroupCategories;
